<template>
    <section class="mb-5">
        <h5 class="font-weight-bold">Upload Receipts</h5>
        <p>Attach all necessary receipts or supporting files associated with this ERP.</p>

        <Alerts :_error="error" :_success="success" :_loading="wait"></Alerts>

        <div class="custom-file mb-2">
            <input type="file" class="custom-file-input" ref="fileInput"
                   @change="handleFileSelection"
                   multiple
                   id="customFile"
                   size="10485760"
                   :accept="allowed_files">
            <!-- Max file input 10MB-->
            <label class="custom-file-label" for="customFile">Select files to upload</label>
        </div>
        <p class="text-muted">Allowed files {{ allowed_files }} | Max size 10MB</p>

        <!-- Display selected files -->
        <section>
            <p v-if="selectedFilesList.length">
                You have selected the following files for upload:
            </p>

            <div class="row">
                <div
                    v-for="(file, index) in selectedFilesList"
                    :key="index"
                    class="col-12 mb-4"
                >
                    <div class="border rounded p-3 shadow-sm h-100">
                        <div class="row align-items-start">
                            <!-- File Preview (Left Side) -->
                            <div class="col-md-7 text-center mb-3 mb-md-0">
                                <div class="text-muted mb-2 small">
                                    Size: {{ file.sizeMb }} MB
                                </div>

                                <!-- Image preview -->
                                <img
                                    v-if="file.type.startsWith('image')"
                                    :src="file.previewURL"
                                    class="img-fluid rounded shadow-sm"
                                    style="max-height: 240px; object-fit: contain;"
                                    alt="Preview"
                                />

                                <!-- PDF preview -->
                                <embed
                                    v-else-if="file.type === 'application/pdf'"
                                    :src="file.previewURL"
                                    type="application/pdf"
                                    width="100%"
                                    style="max-height: 300px;"
                                />

                                <!-- Other file types -->
                                <div v-else class="text-break">
                                    <strong>{{ file.name }}</strong>
                                </div>

                                <div class="mt-2">
                                    <a href="#" class="text-danger"
                                       @click.prevent="removeFileFromList(index)">Remove</a>
                                </div>
                            </div>

                            <!-- Metadata Form (Right Side) -->
                            <div class="col-md-5">
                                <div class="form-group mb-2">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        required
                                        class="form-control"
                                        v-model="file.title"
                                        :placeholder="file.name"
                                    />
                                </div>

                                <div class="form-group mb-2">
                                    <label>Category or Budget <sup class="text-danger">*</sup></label>
                                    <select class="form-control" required v-model="file.category">
                                        <option disabled value="">Select category</option>
                                        <option v-for="budget in erp.budgeting" :value="budget.code" :key="budget.code">
                                            {{ budget.item }}
                                        </option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>

                                <div class="form-group mb-2">
                                    <label>Amount in Receipt <sup class="text-danger">*</sup></label>
                                    <input
                                        required
                                        type="number"
                                        class="form-control"
                                        v-model="file.amount"
                                        placeholder="Enter amount"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- End of file display -->
        <button class="btn btn-outline-primary" @click="uploadFiles()"
                :disabled="wait || !selectedFilesList.length">
            <span v-if="wait">Please wait...</span>
            <span v-else>Upload Selected</span>
        </button>
    </section>
</template>
<script lang="ts">
import Alerts from './Alerts.vue'

export default {
    name: 'FormUpload',
    components: { Alerts },
    props: ['code'],

    data() {
        return {
            axios: this.$http,
            base_url: '',

            erp: null,
            selectedFiles: [],
            selectedFilesList: [],
            allowed_files: '.xls, .xlsx, .csv, .jpg, .jpeg, .png, .gif, .pdf',

            progress: 0,
        }
    },
    mounted() {
        if (this.code) {
            this.loadErp()
        }
    },
    methods: {
        loadErp() {
            this.axios.get(`${this.base_url}/request/${this.code}`).then(response => {
                this.erp = response.data.data
            })
        },
        handleFileSelection(event) {
            this.selectedFiles = event.target.files
            if (this.selectedFiles) {
                for (let i = 0; i < this.selectedFiles.length; i++) {
                    let file = this.selectedFiles[i]
                    if (!this.selectedFilesList.some(f => f.name === file.name)) {
                        file.sizeMb = (file.size / (1024 * 1024)).toFixed(2)
                        file.previewURL = URL.createObjectURL(file)
                        this.selectedFilesList.push(file)
                    }
                }
                this.selectedFiles = null
                this.$refs.fileInput.value = null
            }
        },
        removeFileFromList(index) {
            this.selectedFilesList.splice(index, 1)
        },

        uploadFiles() {
            let formData = new FormData()
            let hasError = false;

            this.selectedFilesList.forEach((file, i) => {
                const title = file.title ? file.title.trim() :  file.name;
                const category = file.category.trim();
                const amount = file.amount;


                if (!category) {
                    alert(`Category is required for file ${file.name}`);
                    hasError = true;
                    return;
                }

                if (amount === null || amount === undefined || amount === '' || isNaN(amount)) {
                    alert(`Amount must be a numeric value for file ${file.name}`);
                    hasError = true;
                    return;
                }

                formData.append(`files[${i}]`, file);
                formData.append(`titles[${i}]`, title);
                formData.append(`categories[${i}]`, category);
                formData.append(`amounts[${i}]`, amount);
            });

            if (hasError) {
                return; // stop submission
            }

            this.wait = true
            this.axios.post(`${this.base_url}/request/${this.code}/docs`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (e) => {
                    this.progress = parseInt(Math.round((e.loaded * 100) / e.total))
                }
            }).then(() => {
                this.progress = 0
                this.success = 'Files uploaded successfully.'
                this.selectedFiles = []
                this.selectedFilesList = []

                this.$emit('reloadDocuments')

            }).catch(error => {
                this.showError(error)

            }).finally(() => {
                this.wait = false
            })
        },
    }
}
</script>